import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "store";
import {
  loadMySubscription,
  setReconciling,
} from "store/slices/subscriptionSlice";
import { fetchUserInfo } from "store/slices/userSlice";

const POLL_INTERVAL_MS = 3_000;
const POLL_MAX_MS = 30_000;

/**
 * status === "PENDING" 또는 isReconciling === true 일 때
 * 3초 간격, 최대 30초까지 GET /me 폴링.
 *
 * 종료 조건:
 * - status가 PENDING이 아닌 다른 값으로 바뀌면 종료
 * - 30초 초과 시 종료 + hasGivenUpRef = true (status가 여전히 PENDING이어도 재시작 안 함)
 *
 * 다음 페이지 진입 시 훅이 새로 마운트되면 hasGivenUpRef가 리셋되어 폴링 재개됨.
 */
export const useSubscriptionReconcile = () => {
  const dispatch = useDispatch<AppDispatch>();
  const status = useSelector((s: RootState) => s.subscription.me?.status);
  const isReconciling = useSelector(
    (s: RootState) => s.subscription.isReconciling
  );
  const startedAtRef = useRef<number | null>(null);
  const hasGivenUpRef = useRef<boolean>(false);

  useEffect(() => {
    const shouldPoll =
      (isReconciling || status === "PENDING") && !hasGivenUpRef.current;
    if (!shouldPoll) {
      // 폴링 중이었다가 구독 상태가 확정(예: PENDING→ACTIVE)되어 종료되는 경우,
      // user.isPremium(=isPremiumUser)도 1회 갱신 — 구독 슬라이스만으론 전역 프리미엄 표시가 stale
      if (startedAtRef.current !== null) {
        dispatch(fetchUserInfo());
      }
      startedAtRef.current = null;
      return;
    }
    if (startedAtRef.current === null) {
      startedAtRef.current = Date.now();
      // 첫 진입 시 즉시 1회 갱신
      dispatch(loadMySubscription());
    }

    const timer = setInterval(() => {
      const elapsed = Date.now() - (startedAtRef.current ?? Date.now());
      if (elapsed > POLL_MAX_MS) {
        hasGivenUpRef.current = true;
        dispatch(setReconciling(false));
        clearInterval(timer);
        return;
      }
      dispatch(loadMySubscription());
    }, POLL_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [status, isReconciling, dispatch]);
};
